import React, { useRef, useState } from 'react';
import FileBadge from './FileBadge';

// Mirrors EventArticleController::ALLOWED_EXTENSIONS / MAX_FILE_BYTES — the
// server re-validates both at register time.
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const MAX_FILE_BYTES = 25 * 1024 * 1024;

async function uploadToAzure(sasUrl, file, onProgress) {
    const { BlockBlobClient } = await import('@azure/storage-blob');
    const client = new BlockBlobClient(sasUrl);

    for (let attempt = 1; attempt <= 3; attempt++) {
        try {
            await client.uploadBrowserData(file, {
                onProgress: (p) => onProgress(Math.floor((p.loadedBytes / file.size) * 100)),
            });
            return;
        } catch (error) {
            if (attempt === 3) throw error;
            await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
        }
    }
}

/**
 * Edit-mode files block: dropzone + per-file direct-to-Azure upload with live
 * progress. Each file registers itself right after its PUT finishes; the
 * registered list state lives in the parent (shared with the preview mode).
 */
export default function FileManager({ files, setFiles, onEdited, confirmGate, confirmRemove, urls, csrf }) {
    const inputRef = useRef(null);
    const [uploads, setUploads] = useState([]); // in-flight: {key, filename, pct, error}
    const [dragOver, setDragOver] = useState(false);

    const patchUpload = (key, patch) => {
        setUploads((current) => current.map((u) => (u.key === key ? { ...u, ...patch } : u)));
    };

    const removeUpload = (key) => {
        setUploads((current) => current.filter((u) => u.key !== key));
    };

    const uploadOne = async (file) => {
        const key = `${file.name}-${Date.now()}-${Math.random()}`;
        const extension = (file.name.split('.').pop() || '').toLowerCase();

        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            setUploads((current) => [...current, {
                key, filename: file.name, pct: 0,
                error: `Unsupported type — allowed: ${ALLOWED_EXTENSIONS.join(', ')}`,
            }]);
            return;
        }

        if (file.size > MAX_FILE_BYTES) {
            setUploads((current) => [...current, {
                key, filename: file.name, pct: 0,
                error: 'File is larger than 25 MB.',
            }]);
            return;
        }

        setUploads((current) => [...current, { key, filename: file.name, pct: 0, error: null }]);

        try {
            const sasResponse = await fetch(urls.uploadUrls, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrf,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ files: [file.name] }),
            });
            if (!sasResponse.ok) throw new Error(`SAS request failed (${sasResponse.status})`);
            const { sasUrls } = await sasResponse.json();
            const target = sasUrls && sasUrls[0];
            if (!target) throw new Error('No upload URL returned.');

            await uploadToAzure(target.sas_url, file, (pct) => patchUpload(key, { pct }));

            const registerResponse = await fetch(urls.fileStore, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrf,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ filename: target.file, mime_type: file.type || null }),
            });
            if (!registerResponse.ok) throw new Error(`Register failed (${registerResponse.status})`);
            const json = await registerResponse.json();

            setFiles(json.files);
            if (onEdited) onEdited(json);
            removeUpload(key);
        } catch (error) {
            patchUpload(key, { error: 'Upload failed. Remove and try again.' });
        }
    };

    const handleFiles = (fileList) => {
        // Snapshot before the (possibly deferred) gate runs: drop DataTransfer
        // lists are invalidated once the event handler returns.
        const selected = [...fileList];
        const start = () => selected.forEach(uploadOne);

        if (confirmGate) {
            confirmGate(start); // one confirmation for the whole batch
        } else {
            start();
        }
    };

    const removeFile = async (filename) => {
        try {
            const response = await fetch(urls.fileDestroy, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrf,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ filename }),
            });
            if (!response.ok) throw new Error(`Delete failed (${response.status})`);
            const json = await response.json();
            setFiles(json.files);
            if (onEdited) onEdited(json);
        } catch (error) {
            window.alert('Could not remove the file. Please try again.');
        }
    };

    return (
        <div className="ea-files">
            <div
                className={`ea-dropzone${dragOver ? ' is-over' : ''}`}
                onClick={() => inputRef.current && inputRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            >
                ⬆&nbsp; Drag files here or <strong>browse</strong>
                <div className="ea-dropzone__hint">{ALLOWED_EXTENSIONS.join(' · ')} — max 25 MB</div>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
                    style={{ display: 'none' }}
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                />
            </div>

            {(files.length > 0 || uploads.length > 0) && (
                <ul className="ea-file-list">
                    {files.map((file) => (
                        <li key={file.filename} className="ea-file-row">
                            <FileBadge filename={file.filename} />
                            <span className="ea-file-main">
                                <span className="ea-file-name">{file.filename}</span>
                                <span className="ea-file-sub ea-file-sub--ok">
                                    <i className="fa-solid fa-circle-check" /> Uploaded
                                </span>
                            </span>
                            <button
                                type="button"
                                className="ea-file-x"
                                title="Remove"
                                onClick={() => (confirmRemove
                                    ? confirmRemove(file.filename, () => removeFile(file.filename))
                                    : removeFile(file.filename))}
                            >×</button>
                        </li>
                    ))}

                    {uploads.map((upload) => (
                        <li key={upload.key} className="ea-file-row">
                            <FileBadge filename={upload.filename} />
                            <span className="ea-file-main">
                                <span className="ea-file-name">{upload.filename}</span>
                                {upload.error ? (
                                    <span className="ea-file-error">{upload.error}</span>
                                ) : (
                                    <>
                                        <span className="ea-file-sub">Uploading directly to Azure… {upload.pct}%</span>
                                        <span className="ea-progress"><i style={{ width: `${upload.pct}%` }} /></span>
                                    </>
                                )}
                            </span>
                            {upload.error && (
                                <button
                                    type="button"
                                    className="ea-file-x"
                                    title="Dismiss"
                                    onClick={() => removeUpload(upload.key)}
                                >×</button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
